// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useState, useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

export interface Beneficiary {
  id: string;
  beneficiary: string;
  weight: string;
}
type InitialBeneficiary = Omit<Beneficiary, 'id'>;

// Define the return type of the hook
interface UseBeneficiaryListReturn {
  beneficiaries: Beneficiary[];
  isValid: boolean;
  addBeneficiary: () => void;
  updateBeneficiary: (id: string, key: keyof Omit<Beneficiary, 'id'>, value: string) => void;
  removeBeneficiary: (id: string) => void;
  hasWeightError: boolean;
}

export const useBeneficiaryList = (
  initialBeneficiaries: InitialBeneficiary[] = [],
): UseBeneficiaryListReturn => {
  const initialDataWithIds: Beneficiary[] = useMemo(() => {
    return initialBeneficiaries.map((b) => ({
      ...b,
      id: uuidv4(),
    }));
  }, [initialBeneficiaries]);
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>(initialDataWithIds);

  const addBeneficiary = useCallback(() => {
    const newBeneficiary: Beneficiary = {
      id: uuidv4(),
      beneficiary: '',
      weight: '',
    };
    setBeneficiaries((prev) => [...prev, newBeneficiary]);
  }, []);
  const updateBeneficiary = useCallback(
    (id: string, key: keyof InitialBeneficiary, value: string) => {
      setBeneficiaries((prev) =>
        prev.map((b) =>
          b.id === id
            ? {
                ...b,
                [key]: value,
              }
            : b,
        ),
      );
    },
    [],
  );

  const removeBeneficiary = useCallback((id: string) => {
    setBeneficiaries((prev) => prev.filter((b) => b.id !== id));
  }, []);

  const isValid = useMemo(() => {
    if (beneficiaries.length === 0) {
      return true;
    }

    return beneficiaries.every((b) => {
      const isBeneficiaryFilled = b.beneficiary.trim().length > 0;
      const isWeightFilled = b.weight.trim().length > 0;

      return isBeneficiaryFilled && isWeightFilled;
    });
  }, [beneficiaries]);
  const hasWeightError = useMemo(() => {
    return beneficiaries.some((b) => {
      const weightNum = parseFloat(b.weight);

      // eslint-disable-next-line no-restricted-globals
      if (!isNaN(weightNum)) {
        return weightNum < 0 || weightNum > 1;
      }

      return false;
    });
  }, [beneficiaries]);
  return {
    beneficiaries,
    isValid,
    addBeneficiary,
    updateBeneficiary,
    removeBeneficiary,
    hasWeightError,
  };
};
